/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatISODateToLocal, formatLastActive } from "../../utils/utils";
import ParticipationRate from "../ParticipationRate";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import MonitorSessionSummaryCards from "./MonitorSessionSummaryCards";
import MonitorSessionTrendChart from "./MonitorSessionTrendChart";
import "../active-users/active-users.css";
import "./MonitorSessions.css";

const LIMIT = 20;
const STATE_KEY = "monitorSessionListState";
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'session_count', label: 'Session count' },
  { key: 'watch_count', label: 'Watch' },
  { key: 'mobile_count', label: 'Mobile' },
  { key: 'other_count', label: 'Other' },
  { key: 'last_session_date', label: 'Last Session Taken' },
  { key: 'last_active', label: 'Last Active (UTC)' },
];

const MonitorSessionList = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();                       // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment) || 'all';     // 'with_watch' | 'without_watch' | 'all'

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [splitCounts, setSplitCounts] = useState({});
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const [summary, setSummary] = useState({ current: {}, prior: {}, deltas: {} });
  const [trend, setTrend] = useState([]);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore list state on return from a session drill-down.
    let restored = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        restored = true;
        setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
        setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
        setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
        setPreset(saved.preset || null);
        setSearchValue(saved.searchValue || ''); setSubscriptionFilter(saved.subscriptionFilter || '');
        setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || ''); setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    if (!restored) {
      // Default to the last 30 days (matches /user-pause-count) so the date filter
      // is always a concrete window, not a silent all-time query.
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [token]);

  // Re-fetch when the global segment changes (skip initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [segment]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page,
    }));
  }, [fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page]);

  const qs = (p, f, t, s, sf, so, sub) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}&subscription_status=${sub}&user_segment=${userSegment}`;
  const rangeQs = (f, t, s, sub) =>
    `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&user_segment=${userSegment}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const applyList = (d) => {
    setData(d.data || []);
    setTotalItem(d.totalItem || 0);
    setTotalUsers(d.userCount?.[0]?.unique_user_count || 0);
    setTotalUserBase(d.totalUserCount || 0);
    setSplitCounts(d.splitUserCounts || {});
    setSubscribedUserCount(d.subscribedUserCount || 0);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/user-monitor-sessions?${qs(p, f, t, s, sf, so, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, sub);
    try {
      const [list, sum, tr] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/user-monitor-sessions?${qs(p, f, t, s, sf, so, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/monitor-session-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/monitor-session-trend?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary(sum.data.data || { current: {}, prior: {}, deltas: {} });
      setTrend(tr.data.data || []);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', sortField, sortOrder, '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/monitor-session-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&sortField=${sortField}&sortOrder=${sortOrder}&subscription_status=${subscriptionFilter}&user_segment=${userSegment}`,
      auth());
    // Format dates to match the on-screen columns (FR-019).
    return (res.data.data || []).map((r) => ({
      ...r,
      last_session_date: r.last_session_date ? formatISODateToLocal(r.last_session_date) : '',
      last_active: r.last_active ? formatLastActive(r.last_active) : '',
    }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);
  const n = (v) => Number(v || 0).toLocaleString();
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  const totalSessions = summary.current?.total_sessions ?? Number(users?.[0]?.total_session_count || 0);
  const totalUsersVal = summary.current?.total_users ?? totalUsers;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Monitor sessions</b></span>
            <span className="au-route">/user-monitor-sessions</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Monitor Sessions</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the monitor sessions data. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter)} />
          ) : (
            <>
              {/* Summary before detail */}
              <MonitorSessionSummaryCards
                totalSessions={totalSessions}
                totalUsers={totalUsersVal}
                totalUserBase={totalUserBase}
                subscribedUserCount={subscribedUserCount}
                deltas={summary.deltas}
              />

              {/* Device splits (preserved) */}
              <div className="au-cards" data-testid="ms-device-cards">
                <div className="au-card"><span className="au-card__label">From Watch</span><span className="au-card__value tnum">{n(users?.[0]?.total_watch_count)}</span><ParticipationRate className="au-card__sub" count={splitCounts.watch_user_count} total={totalUserBase} /></div>
                <div className="au-card"><span className="au-card__label">From Mobile</span><span className="au-card__value tnum">{n(users?.[0]?.total_mobile_count)}</span><ParticipationRate className="au-card__sub" count={splitCounts.mobile_user_count} total={totalUserBase} /></div>
                <div className="au-card"><span className="au-card__label">From Other</span><span className="au-card__value tnum">{n(users?.[0]?.total_other_count)}</span><ParticipationRate className="au-card__sub" count={splitCounts.other_user_count} total={totalUserBase} /></div>
              </div>

              <MonitorSessionTrendChart data={trend} />

              {/* Filters */}
              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search users" />
                    <select className="au-select" name="subscriptionFilter" value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="ms-range-note">
                The date range filters sessions by their <b>activity date (UTC)</b> — the session timestamp shown per user as “Last Session Taken”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all sessions (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No sessions in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No users match the current filters.' : 'There are no monitor sessions to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user monitor sessions</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="monitor-sessions.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table ms-table" data-testid="ms-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th>
                          <SortTh field="session_count" label="Session count" />
                          <SortTh field="watch_count" label="Watch" />
                          <SortTh field="mobile_count" label="Mobile" />
                          <SortTh field="other_count" label="Other" />
                          <SortTh field="last_session_date" label="Last Session Taken" />
                          <SortTh field="last_active" label="Last Active (UTC)" />
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.id}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{user.name}</td>
                            <td data-label="Session count" className="tnum"><Link className="au-link" to={`/monitor-sessions/${btoa(user.id)}`}>{user.session_count}</Link></td>
                            <td data-label="Watch" className="tnum">{user.watch_count || 0}</td>
                            <td data-label="Mobile" className="tnum">{user.mobile_count || 0}</td>
                            <td data-label="Other" className="tnum">{user.other_count || 0}</td>
                            <td data-label="Last Session Taken">{user.last_session_date ? formatISODateToLocal(user.last_session_date) : '-'}</td>
                            <td data-label="Last Active (UTC)">{user.last_active ? formatLastActive(user.last_active) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitorSessionList;
